import * as React from 'react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { Order } from '@klareospace/pharos-lib-types';

import { TagsInput } from '&/shared/components';
import type { TagsInputHandle } from '&/shared/components';

import { formatTags } from '../utils';
import { useInlineOrderFieldEdit } from '../hooks/use-inline-order-field-edit';
import type { OrderFieldEditState, OrderFieldLockReason } from '../hooks/use-order-editability';
import { EditableFieldWrapper } from './editable-field-wrapper';
import { InlineEditActions } from './inline-edit-actions';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TagsInlineEditorProps {
  readonly fieldState: OrderFieldEditState;
  readonly lockReason: OrderFieldLockReason | null;
  readonly order: Order;
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Order tags — creatable chip input, built on the shared `TagsInput`. */
export function TagsInlineEditor({ fieldState, lockReason, order }: TagsInlineEditorProps): React.JSX.Element {
  const { t } = useTranslation('orders');
  const tagsInputRef = useRef<TagsInputHandle>(null);

  const { draft, handleCancel, handleConfirm, isEditing, isPending, setDraft, startEdit } = useInlineOrderFieldEdit<
    string[]
  >({
    buildPatch: (value) => ({ tags: value }),
    order,
    successKey: 'drawer.edit.content.success',
    value: order.tags ?? [],
  });

  /**
   * Folds any text still sitting in the input into the draft before submitting — otherwise a
   * typed-but-not-Enter-confirmed tag is silently dropped when the user clicks confirm directly.
   */
  function handleConfirmClick(): void {
    const nextDraft = tagsInputRef.current?.commitPendingTag() ?? draft;
    handleConfirm(nextDraft);
  }

  if (isEditing) {
    return (
      <div className="mt-1 flex flex-col gap-1.5">
        <TagsInput
          ariaLabel={t('drawer.edit.content.tagsAriaLabel')}
          disabled={isPending}
          onChange={setDraft}
          placeholder={t('drawer.edit.content.tagsPlaceholder')}
          ref={tagsInputRef}
          removeTagAriaLabel={(tag): string => t('drawer.edit.content.removeTag', { tag })}
          value={draft}
        />
        <div className="flex items-center justify-end gap-1">
          <InlineEditActions isPending={isPending} onCancel={handleCancel} onConfirm={handleConfirmClick} />
        </div>
      </div>
    );
  }

  return (
    <EditableFieldWrapper fieldState={fieldState} lockReason={lockReason} onEdit={startEdit}>
      <span className="text-sm text-foreground">{formatTags(order.tags)}</span>
    </EditableFieldWrapper>
  );
}
